import React from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Copy, Share, ArrowLeft } from 'lucide-react';

interface BaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  showBackButton?: boolean;
  onBack?: () => void;
  className?: string;
}

const BaseModal: React.FC<BaseModalProps> = ({
  isOpen,
  onClose,
  children,
  title,
  showBackButton,
  onBack,
  className = '',
}) => {
  const { t } = useTranslation();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={`bg-surface-muted border-border/30 text-foreground max-w-md ${className}`}
      >
        <DialogHeader className="relative">
          {showBackButton && (
            <button
              data-test-id="modal-back-btn"
              onClick={onBack}
              className="absolute left-0 top-0 p-2 text-muted-foreground hover:text-foreground transition-smooth"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="ml-2 text-sm">{t('actions.back')}</span>
            </button>
          )}

          {title && (
            <DialogTitle className="text-center text-foreground font-bold text-xl">
              {title}
            </DialogTitle>
          )}
        </DialogHeader>

        <div className="mt-6">{children}</div>
      </DialogContent>
    </Dialog>
  );
};

// League Created Modal
interface LeagueCreatedModalProps {
  isOpen: boolean;
  onClose: () => void;
  pin: string;
  onViewLeague: () => void;
}

export const LeagueCreatedModal: React.FC<LeagueCreatedModalProps> = ({
  isOpen,
  onClose,
  pin,
  onViewLeague,
}) => {
  const handleCopy = () => {
    navigator.clipboard.writeText(pin);
  };

  const { t } = useTranslation();

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: t('modal.leagueCreated.shareTitle'),
        text: t('modal.leagueCreated.shareText', { pin }),
      });
    }
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('modal.leagueCreated.title')}
      showBackButton
      onBack={onClose}
    >
      <div className="text-center space-y-6">
        <p className="text-muted-foreground">{t('modal.leagueCreated.pinMessage')}</p>

        {/* PIN Display */}
        <div className="flex justify-center gap-2">
          {pin.split('').map((char, index) => (
            <div
              key={index}
              className="w-12 h-12 bg-surface rounded-lg flex items-center justify-center text-foreground font-bold text-xl"
            >
              {char}
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button
            data-test-id="copy-pin-btn"
            variant="secondary"
            onClick={handleCopy}
            className="flex-1 gap-2"
          >
            <Copy className="w-4 h-4" />
            {t('modal.leagueCreated.copy')}
          </Button>
          <Button
            data-test-id="share-pin-btn"
            variant="default"
            onClick={handleShare}
            className="flex-1 gap-2"
          >
            <Share className="w-4 h-4" />
            {t('modal.leagueCreated.share')}
          </Button>
        </div>

        <Button
          data-test-id="view-league-btn"
          variant="default"
          onClick={onViewLeague}
          className="w-full"
        >
          {t('modal.leagueCreated.viewLeague')}
        </Button>
      </div>
    </BaseModal>
  );
};

// Picks Submitted Modal
interface PicksSubmittedModalProps {
  isOpen: boolean;
  onClose: () => void;
  potentialWinnings: string;
  onAddToBetSlip: () => void;
  onReturnToLobby: () => void;
}

export const PicksSubmittedModal: React.FC<PicksSubmittedModalProps> = ({
  isOpen,
  onClose,
  potentialWinnings,
  onAddToBetSlip,
  onReturnToLobby,
}) => {
  const { t } = useTranslation();

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} title={t('modal.picksSubmitted.title')}>
      <div className="text-center space-y-6">
        <p className="text-muted-foreground">
          {t('modal.picksSubmitted.message')}
          <br />
          {t('modal.picksSubmitted.editMessage')}
        </p>

        <div className="bg-surface rounded-xl p-4">
          <p className="text-muted-foreground text-sm mb-2">
            {t('modal.picksSubmitted.likeYourPicks')}
          </p>
          <p className="text-muted-foreground text-sm mb-4">
            {t('modal.picksSubmitted.buildBetSlip')}
          </p>

          <div className="space-y-2">
            <p className="text-foreground font-bold text-sm">
              {t('modal.picksSubmitted.youCouldWin')}
            </p>
            <p className="text-foreground font-bold text-3xl">{potentialWinnings}</p>
            <p className="text-muted-foreground text-sm">
              {t('modal.picksSubmitted.fromBet', { amount: '10' })}
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <Button variant="default" onClick={onAddToBetSlip} className="w-full">
            {t('modal.picksSubmitted.addToBetSlip')}
          </Button>
          <Button variant="secondary" onClick={onReturnToLobby} className="w-full">
            {t('modal.picksSubmitted.returnToLobby')}
          </Button>
        </div>
      </div>
    </BaseModal>
  );
};

// Clear Selections Modal
interface ClearSelectionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onClearPicks: () => void;
}

export const ClearSelectionsModal: React.FC<ClearSelectionsModalProps> = ({
  isOpen,
  onClose,
  onClearPicks,
}) => {
  const { t } = useTranslation();

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} title={t('modal.clearSelections.title')}>
      <div className="text-center space-y-6">
        <div className="space-y-3">
          <Button variant="default" onClick={onClearPicks} className="w-full">
            {t('modal.clearSelections.confirm')}
          </Button>
          <Button variant="secondary" onClick={onClose} className="w-full">
            {t('modal.clearSelections.cancel')}
          </Button>
        </div>
      </div>
    </BaseModal>
  );
};

export default BaseModal;
